import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Landmark,
  Plus,
  Search,
  DollarSign,
  Calendar,
  Building2,
  RefreshCw,
  X,
  Check,
  Upload,
  FileUp,
  FileText,
  Loader2,
  Ban,
  AlertTriangle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Componentes refatorados
import { 
  SimulacaoSection, 
  ContratosFilter, 
  TotaisCliente,
  ContratosList,
  JornadaCreditoCliente
} from "@/components/monitoramento";

const tipoOptions = [
  { value: "INTERNO", label: "Interno" },
  { value: "EXTERNO", label: "Externo" },
];

const situacaoOptions = [
  { value: "ATIVO", label: "Ativo" },
  { value: "QUITADO", label: "Quitado" },
  { value: "PORTABILIDADE", label: "Portabilidade" },
  { value: "REFINANCIADO", label: "Refinanciado" },
  { value: "SUSPENSO", label: "Suspenso" },
  { value: "CANCELADO", label: "Cancelado" },
];

const tipoOperacaoOptions = [
  { value: "EMPRÉSTIMO", label: "Empréstimo" },
  { value: "CARTÃO CONSIGNADO", label: "Cartão Consignado" },
  { value: "CARTÃO BENEFÍCIO", label: "Cartão Benefício" },
  { value: "PORTABILIDADE", label: "Portabilidade" },
  { value: "REFINANCIAMENTO", label: "Refinanciamento" },
  { value: "SAQUE ANIVERSÁRIO", label: "Saque Aniversário" },
];

const formatCurrency = (value) => {
  if (!value && value !== 0) return "-";
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

// Formatar valor para padrão brasileiro (entrada de usuário)
const formatValorBR = (value) => {
  if (!value) return "";
  // Remove tudo exceto números, vírgula e ponto
  let cleaned = value.replace(/[^\d,.-]/g, "");
  return cleaned;
};

// Formatar valor ao sair do campo (blur)
const formatValorBROnBlur = (value) => {
  if (!value) return "0,00";
  // Remove pontos de milhar e converte vírgula para ponto para processar
  let numStr = value.replace(/\./g, "").replace(",", ".");
  let num = parseFloat(numStr);
  if (isNaN(num)) return "0,00";
  // Formata com 2 casas decimais e vírgula
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  } catch {
    return dateStr;
  }
};

const getSituacaoBadge = (situacao) => {
  const variants = {
    ATIVO: "bg-green-100 text-green-800",
    QUITADO: "bg-blue-100 text-blue-800",
    PORTABILIDADE: "bg-purple-100 text-purple-800",
    REFINANCIADO: "bg-orange-100 text-orange-800",
    SUSPENSO: "bg-yellow-100 text-yellow-800",
    CANCELADO: "bg-red-100 text-red-800",
  };
  return <Badge className={cn(variants[situacao] || "bg-gray-100 text-gray-800")}>{situacao}</Badge>;
};

const getTipoIcon = (tipo) => {
  if (tipo === "INTERNO") return <Building2 className="h-4 w-4 text-blue-600" />;
  return <ArrowRightLeft className="h-4 w-4 text-purple-600" />;
};

// Formatar CPF
const formatCPF = (cpf) => {
  if (!cpf) return "";
  const numbers = cpf.replace(/\D/g, "");
  if (numbers.length !== 11) return cpf;
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

export default function MonitoramentoContratos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [situacaoFiltro, setSituacaoFiltro] = useState("");
  const [bancos, setBancos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContrato, setEditingContrato] = useState(null);
  const [cpfBusca, setCpfBusca] = useState("");
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  // Estado para o cliente principal da tela (não do modal)
  const [cpfBuscaPrincipal, setCpfBuscaPrincipal] = useState("");
  const [clienteSelecionadoPrincipal, setClienteSelecionadoPrincipal] = useState(null);
  const [buscandoClientePrincipal, setBuscandoClientePrincipal] = useState(false);
  // Instituições do cliente (até 2)
  const [instituicoesCliente, setInstituicoesCliente] = useState([]);
  const [instituicaoSelecionada, setInstituicaoSelecionada] = useState("");
  // Percentual de desconto selecionado (para cálculo sem taxa) - Padrão 20%
  const [percentualDesconto, setPercentualDesconto] = useState("20");
  
  // Estados para Linha de Financiamento na Simulação
  const [correspondentes, setCorrespondentes] = useState([]);
  const [tabelasComissao, setTabelasComissao] = useState([]);
  // Estado para contratos ocultos (INTERNOS que o usuário "excluiu")
  const [contratosOcultos, setContratosOcultos] = useState({});
  
  // Estado para Checklist de Análise
  const [checklistAnalise, setChecklistAnalise] = useState({});
  const [loadingChecklist, setLoadingChecklist] = useState({});
  const contratosAnalisadosRef = useRef(new Set());
  
  const [totais, setTotais] = useState({
    totalContratos: 0,
    totalParcelas: 0,
    totalSaldoDevedor: 0,
    totalLiberado: 0
  });

  // Estados para importação de extrato
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importando, setImportando] = useState(false);
  const [dadosExtraidos, setDadosExtraidos] = useState(null);
  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const [etapaImportacao, setEtapaImportacao] = useState('upload'); // 'upload', 'preview', 'success'
  const [tipoExtrato, setTipoExtrato] = useState('SIAPE'); // Tipo de extrato selecionado

  // Estados para simulação de operações
  const [simulacoes, setSimulacoes] = useState({
    margemAumento: { ativo: false, valorParc: "", banco: "", tabela: "", qtdeParcela: "84", valorLiquido: "", margemDisponivel: "0,00" },
    margemIR: { ativo: false, valorParc: "", banco: "", tabela: "", qtdeParcela: "84", valorLiquido: "", margemDisponivel: "0,00" },
    margemLivre: { 
      ativo: false, 
      valorParc: "", 
      banco: "", 
      instituicao: "",
      correspondente: "",
      tabela: "", 
      tabelaId: "",
      produto: "",
      qtdeParcela: "84", 
      valorLiquido: "", 
      margemDisponivel: "0,00" 
    },
    cartaoRMC: { 
      ativo: false, 
      valorParc: "", 
      banco: "", 
      instituicao: "",
      correspondente: "",
      tabela: "", 
      tabelaId: "",
      produto: "",
      qtdeParcela: "84", 
      valorLiquido: "", 
      margemDisponivel: "0,00" 
    },
    cartaoBeneficio: { 
      ativo: false, 
      valorParc: "", 
      banco: "", 
      instituicao: "",
      correspondente: "",
      tabela: "", 
      tabelaId: "",
      produto: "",
      qtdeParcela: "84", 
      valorLiquido: "", 
      margemDisponivel: "0,00" 
    },
    seguro: { ativo: false, seguradora: "", valorParc: "", taxaJuros: "", margemDisponivel: "0,00" }
  });

  // A presença da margem no extrato (inclusive 0,00) ou de um contrato de
  // cartão significa que o Vanguard trouxe esse bloco. Mantê-lo ativo evita
  // esconder os dados importados atrás do interruptor da simulação.
  const ativarSimulacoesImportadas = (beneficio = {}, contratosCliente = []) => {
    const possuiCampoRMC = Object.prototype.hasOwnProperty.call(beneficio, "margem_cartao_rmc");
    const possuiCampoBeneficio = Object.prototype.hasOwnProperty.call(beneficio, "margem_cartao_beneficio");
    const possuiCampoLivre = ["margem_livre", "margem_disponivel", "margem_consignavel"]
      .some(campo => Object.prototype.hasOwnProperty.call(beneficio, campo));

    const tipos = contratosCliente.map(contrato =>
      String(contrato.tipo_operacao || contrato.produto || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
    );
    const possuiCartaoBeneficio = tipos.some(tipo => tipo.includes("CARTAO") && tipo.includes("BENEF"));
    const possuiCartaoRMC = tipos.some(tipo =>
      tipo.includes("RMC") || (tipo.includes("CARTAO") && !tipo.includes("BENEF"))
    );

    setSimulacoes(prev => ({
      ...prev,
      margemLivre: { ...prev.margemLivre, ativo: prev.margemLivre.ativo || possuiCampoLivre },
      cartaoRMC: { ...prev.cartaoRMC, ativo: prev.cartaoRMC.ativo || possuiCampoRMC || possuiCartaoRMC },
      cartaoBeneficio: {
        ...prev.cartaoBeneficio,
        ativo: prev.cartaoBeneficio.ativo || possuiCampoBeneficio || possuiCartaoBeneficio
      }
    }));
  };

  // Form state
  const [formData, setFormData] = useState({
    cliente_id: "",
    numero_contrato: "",
    tipo: "EXTERNO",
    tipo_operacao: "",
    banco_codigo: "",
    banco_nome: "",
    instituicao: "",
    valor_parcela: "",
    quantidade_parcelas: "",
    parcela_atual: "",
    parcelas_restantes: "",
    taxa_juros: "",
    valor_liberado: "",
    saldo_devedor: "",
    data_inicio: "",
    data_fim: "",
    data_averbacao: "",
    situacao: "ATIVO",
    observacoes: "",
    margem_disponivel: "",
    comissao_calculada: ""
  });

  const fetchContratos = useCallback(async (clienteId = null, instituicao = null) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // MULTI-TENANCY: Adicionar empresa_id se disponível
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      if (empresaAtual?.id) {
        params.append("empresa_id", empresaAtual.id);
      }
      
      if (clienteId) {
        params.append("cliente_id", clienteId);
      } else if (clienteSelecionadoPrincipal?.id) {
        params.append("cliente_id", clienteSelecionadoPrincipal.id);
      }
      // Não filtrar por instituição automaticamente - buscar todos os contratos do cliente
      // O filtro de instituição só será usado se explicitamente necessário
      if (search) params.append("search", search);
      if (tipoFiltro && tipoFiltro !== "ALL") params.append("tipo", tipoFiltro);
      if (situacaoFiltro && situacaoFiltro !== "ALL") params.append("situacao", situacaoFiltro);
      params.append("per_page", "100");

      const response = await axios.get(`${API}/contratos?${params.toString()}`);
      const cts = response.data.contratos || [];
      setContratos(cts);
      ativarSimulacoesImportadas(clienteSelecionadoPrincipal?.beneficio || {}, cts);
      
      // Calcular totais
      setTotais({
        totalContratos: cts.length,
        totalParcelas: cts.reduce((sum, c) => sum + (c.valor_parcela || 0), 0),
        totalSaldoDevedor: cts.reduce((sum, c) => sum + (c.saldo_devedor || 0), 0),
        totalLiberado: cts.reduce((sum, c) => sum + (c.valor_liberado || 0), 0)
      });
    } catch (error) {
      console.error("Erro ao carregar contratos:", error);
      toast.error("Erro ao carregar contratos");
    } finally {
      setLoading(false);
    }
  }, [search, tipoFiltro, situacaoFiltro, clienteSelecionadoPrincipal]);
  
  // Efeito para analisar automaticamente os contratos quando carregados
  useEffect(() => {
    if (contratos.length > 0 && clienteSelecionadoPrincipal) {
      // Limpar análises anteriores quando mudar de cliente
      contratosAnalisadosRef.current.clear();
      setChecklistAnalise({});
      
      // Pequeno delay para garantir que o estado está estável
      const timer = setTimeout(() => {
        analisarTodosContratos(contratos);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [contratos, clienteSelecionadoPrincipal?.id]);
  
  // Função para analisar todos os contratos automaticamente (REFIN + PORTABILIDADE)
  const analisarTodosContratos = async (listaContratos) => {
    for (const contrato of listaContratos) {
      // Analisar REFINANCIAMENTO se ainda não foi analisado
      const keyRefin = `${contrato.id}_REFINANCIAMENTO`;
      if (!contratosAnalisadosRef.current.has(keyRefin)) {
        contratosAnalisadosRef.current.add(keyRefin);
        await analisarContratoSilencioso(contrato, "REFINANCIAMENTO");
      }
      
      // Analisar PORTABILIDADE se ainda não foi analisado
      const keyPort = `${contrato.id}_PORTABILIDADE`;
      if (!contratosAnalisadosRef.current.has(keyPort)) {
        contratosAnalisadosRef.current.add(keyPort);
        await analisarContratoSilencioso(contrato, "PORTABILIDADE");
      }
    }
  };
  
  // Versão silenciosa da análise (sem toasts de erro)
  const analisarContratoSilencioso = async (contrato, tipoOperacao) => {
    const key = `${contrato.id}_${tipoOperacao}`;
    
    try {
      // Buscar dados do banco com parâmetros
      const bancoCodigo = contrato.banco_codigo || contrato.banco_nome?.split(' - ')[0];
      const bancoNome = contrato.banco_nome?.split(' - ')[1] || contrato.banco_nome || "";
      if (!bancoCodigo && !bancoNome) return null;
      
      // Buscar banco pelo código ou nome
      const bancosRes = await axios.get(`${API}/bancos`);
      const banco = bancosRes.data.find(b => 
        b.codigo === bancoCodigo || 
        b.nome?.toUpperCase().includes(bancoNome.toUpperCase()) ||
        bancoNome.toUpperCase().includes(b.nome?.toUpperCase() || "") ||
        b.nome?.includes(contrato.banco_nome) ||
        contrato.banco_nome?.includes(b.nome)
      );
      
      if (!banco) return null;
      
      // Buscar parâmetros do banco para a instituição e tipo de operação
      // Helper para extrair nome da instituição
      const getInstNome = (inst) => {
        if (!inst) return null;
        if (typeof inst === 'string') return inst;
        if (typeof inst === 'object') return inst.nome || inst.instituicao || null;
        return null;
      };
      const instituicao = contrato.instituicao || getInstNome(clienteSelecionadoPrincipal?.primeira_instituicao);
      
      // Extrai apenas o código numérico da instituição (ex: "037" de "037 INSS - PREVIDÊNCIA SOCIAL")
      const codigoInstituicao = instituicao?.match(/^\d{3}/)?.[0] || instituicao?.split(' ')[0]?.replace(/\D/g, '')?.padStart(3, '0');
      
      // Normaliza o tipo de operação para comparação
      const normalizarTipo = (tipo) => {
        if (!tipo) return '';
        return tipo.toString().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
      };
      const tipoNormalizado = normalizarTipo(tipoOperacao);
      
      const parametro = banco.parametros?.find(p => {
        const instParam = (p.instituicao || "").split(' - ')[0]?.trim();
        const mesmaInst = instParam === codigoInstituicao || 
                          p.instituicao === instituicao ||
                          !p.instituicao;
        const tipoParamNorm = normalizarTipo(p.tipoOperacao);
        const mesmoTipo = tipoParamNorm === tipoNormalizado || 
                          tipoParamNorm.includes(tipoNormalizado.replace('_', '')) ||
                          tipoNormalizado.includes(tipoParamNorm.replace('_', ''));
        return mesmaInst && mesmoTipo && p.ativo !== false;
      });
      
      if (!parametro) return null;
      
      // Calcular idade do cliente
      let idadeCliente = null;
      if (clienteSelecionadoPrincipal?.data_nascimento) {
        const nascimento = new Date(clienteSelecionadoPrincipal.data_nascimento);
        const hoje = new Date();
        idadeCliente = Math.floor((hoje - nascimento) / (365.25 * 24 * 60 * 60 * 1000));
      }
      
      // Dados do contrato
      const parcelasPagas = contrato.parcela_atual || 0;
      const valorParcela = contrato.valor_parcela || 0;
      const saldoDevedor = contrato.saldo_devedor || 0;
      
      // Converter parâmetros para números
      const parseNum = (val) => {
        if (!val) return null;
        return parseFloat(val.toString().replace(/[^\d.,]/g, '').replace(',', '.'));
      };
      
      // Análise de cada critério
      const criterios = [];
      
      // 1. Idade Mínima
      const idadeMin = parseNum(parametro.idadeMinima);
      if (idadeMin !== null) {
        criterios.push({
          nome: "Idade Mín.",
          valorParametro: `${idadeMin} anos`,
          valorContrato: idadeCliente ? `${idadeCliente} anos` : "N/I",
          aprovado: idadeCliente !== null && idadeCliente >= idadeMin
        });
      }
      
      // 2. Idade Máxima
      const idadeMax = parseNum(parametro.idadeMaxima);
      if (idadeMax !== null) {
        criterios.push({
          nome: "Idade Máx.",
          valorParametro: `${idadeMax} anos`,
          valorContrato: idadeCliente ? `${idadeCliente} anos` : "N/I",
          aprovado: idadeCliente !== null && idadeCliente <= idadeMax
        });
      }
      
      // 3. Parcelas Pagas
      const parcelasMinimas = parseNum(parametro.parcelasPagas);
      if (parcelasMinimas !== null) {
        criterios.push({
          nome: "Parcelas Pagas",
          valorParametro: `${parcelasMinimas}`,
          valorContrato: `${parcelasPagas}`,
          aprovado: parcelasPagas >= parcelasMinimas
        });
      }
      
      // 4. Parcela Mínima
      const parcelaMin = parseNum(parametro.valorParcelaMinima);
      if (parcelaMin !== null) {
        criterios.push({
          nome: "Parcela Mín.",
          valorParametro: `R$ ${parcelaMin.toFixed(2)}`,
          valorContrato: `R$ ${valorParcela.toFixed(2)}`,
          aprovado: valorParcela >= parcelaMin
        });
      }
      
      // 5. Valor Troco
      const valorTrocoMin = parseNum(parametro.valorTroco);
      if (valorTrocoMin !== null) {
        const valorTrocoEstimado = saldoDevedor * 0.3;
        criterios.push({
          nome: "Valor Troco",
          valorParametro: `R$ ${valorTrocoMin.toFixed(2)}`,
          valorContrato: `R$ ${valorTrocoEstimado.toFixed(2)} (est.)`,
          aprovado: valorTrocoEstimado >= valorTrocoMin
        });
      }
      
      // 6. Saldo Devedor Mínimo
      const saldoMin = parseNum(parametro.saldoDevedorMinimo);
      if (saldoMin !== null) {
        criterios.push({
          nome: "Saldo Dev. Mín.",
          valorParametro: `R$ ${saldoMin.toFixed(2)}`,
          valorContrato: `R$ ${saldoDevedor.toFixed(2)}`,
          aprovado: saldoDevedor >= saldoMin
        });
      }
      
      if (criterios.length === 0) return null;
      
      // Determinar status geral
      const todosAprovados = criterios.every(c => c.aprovado);
      
      const resultado = {
        banco: banco.nome,
        tipoOperacao,
        instituicao,
        criterios,
        statusGeral: todosAprovados ? "APROVADO" : "REPROVADO",
        totalCriterios: criterios.length,
        aprovados: criterios.filter(c => c.aprovado).length,
        reprovados: criterios.filter(c => !c.aprovado).length
      };
      
      setChecklistAnalise(prev => ({ ...prev, [key]: resultado }));
      return resultado;
      
    } catch (error) {
      console.error("Erro ao analisar contrato:", error);
      return null;
    }
  };

  // Função para analisar contrato contra parâmetros do banco
  const analisarContratoRefin = async (contrato, tipoOperacao) => {
    const key = `${contrato.id}_${tipoOperacao}`;
    setLoadingChecklist(prev => ({ ...prev, [key]: true }));
    
    try {
      // Buscar dados do banco com parâmetros
      const bancoCodigo = contrato.banco_codigo || contrato.banco_nome?.split(' - ')[0];
      if (!bancoCodigo) {
        toast.error("Código do banco não encontrado");
        return null;
      }
      
      // Buscar banco pelo código
      const bancosRes = await axios.get(`${API}/bancos`);
      const banco = bancosRes.data.find(b => 
        b.codigo === bancoCodigo || 
        b.nome?.includes(contrato.banco_nome) ||
        contrato.banco_nome?.includes(b.nome)
      );
      
      if (!banco) {
        toast.error(`Banco ${bancoCodigo} não encontrado`);
        return null;
      }
      
      // Buscar parâmetros do banco para a instituição e tipo de operação
      // Helper para extrair nome da instituição
      const getInstituicaoNome = (inst) => {
        if (!inst) return null;
        if (typeof inst === 'string') return inst;
        if (typeof inst === 'object') return inst.nome || inst.instituicao || null;
        return null;
      };
      const instituicao = contrato.instituicao || getInstituicaoNome(clienteSelecionadoPrincipal?.primeira_instituicao);
      const parametro = banco.parametros?.find(p => 
        p.tipoOperacao === tipoOperacao &&
        (p.instituicao === instituicao || !p.instituicao)
      );
      
      if (!parametro) {
        toast.error(`Parâmetros de ${tipoOperacao} não encontrados para este banco`);
        return null;
      }
      
      // Calcular idade do cliente
      let idadeCliente = null;
      if (clienteSelecionadoPrincipal?.data_nascimento) {
        const nascimento = new Date(clienteSelecionadoPrincipal.data_nascimento);
        const hoje = new Date();
        idadeCliente = Math.floor((hoje - nascimento) / (365.25 * 24 * 60 * 60 * 1000));
      }
      
      // Dados do contrato
      const parcelasPagas = contrato.parcela_atual || 0;
      const valorParcela = contrato.valor_parcela || 0;
      const saldoDevedor = contrato.saldo_devedor || 0;
      
      // Converter parâmetros para números
      const parseNum = (val) => {
        if (!val) return null;
        return parseFloat(val.toString().replace(/[^\d.,]/g, '').replace(',', '.'));
      };
      
      // Análise de cada critério
      const criterios = [];
      
      // 1. Idade Mínima
      const idadeMin = parseNum(parametro.idadeMinima);
      if (idadeMin !== null) {
        criterios.push({
          nome: "Idade Mín.",
          valorParametro: `${idadeMin} anos`,
          valorContrato: idadeCliente ? `${idadeCliente} anos` : "N/I",
          aprovado: idadeCliente !== null && idadeCliente >= idadeMin
        });
      }
      
      // 2. Idade Máxima
      const idadeMax = parseNum(parametro.idadeMaxima);
      if (idadeMax !== null) {
        criterios.push({
          nome: "Idade Máx.",
          valorParametro: `${idadeMax} anos`,
          valorContrato: idadeCliente ? `${idadeCliente} anos` : "N/I",
          aprovado: idadeCliente !== null && idadeCliente <= idadeMax
        });
      }
      
      // 3. Parcelas Pagas
      const parcelasMinimas = parseNum(parametro.parcelasPagas);
      if (parcelasMinimas !== null) {
        criterios.push({
          nome: "Parcelas Pagas",
          valorParametro: `${parcelasMinimas}`,
          valorContrato: `${parcelasPagas}`,
          aprovado: parcelasPagas >= parcelasMinimas
        });
      }
      
      // 4. Parcela Mínima
      const parcelaMin = parseNum(parametro.valorParcelaMinima);
      if (parcelaMin !== null) {
        criterios.push({
          nome: "Parcela Mín.",
          valorParametro: `R$ ${parcelaMin.toFixed(2)}`,
          valorContrato: `R$ ${valorParcela.toFixed(2)}`,
          aprovado: valorParcela >= parcelaMin
        });
      }
      
      // 5. Valor Troco
      const valorTrocoMin = parseNum(parametro.valorTroco);
      if (valorTrocoMin !== null) {
        // Calcular valor troco estimado (30% do saldo devedor como exemplo)
        const valorTrocoEstimado = saldoDevedor * 0.3;
        criterios.push({
          nome: "Valor Troco",
          valorParametro: `R$ ${valorTrocoMin.toFixed(2)}`,
          valorContrato: `R$ ${valorTrocoEstimado.toFixed(2)} (est.)`,
          aprovado: valorTrocoEstimado >= valorTrocoMin
        });
      }
      
      // 6. Saldo Devedor Mínimo
      const saldoMin = parseNum(parametro.saldoDevedorMinimo);
      if (saldoMin !== null) {
        criterios.push({
          nome: "Saldo Dev. Mín.",
          valorParametro: `R$ ${saldoMin.toFixed(2)}`,
          valorContrato: `R$ ${saldoDevedor.toFixed(2)}`,
          aprovado: saldoDevedor >= saldoMin
        });
      }
      
      // Determinar status geral
      const todosAprovados = criterios.every(c => c.aprovado);
      const algumReprovado = criterios.some(c => !c.aprovado);
      
      const resultado = {
        banco: banco.nome,
        tipoOperacao,
        instituicao,
        criterios,
        statusGeral: todosAprovados ? "APROVADO" : "REPROVADO",
        totalCriterios: criterios.length,
        aprovados: criterios.filter(c => c.aprovado).length,
        reprovados: criterios.filter(c => !c.aprovado).length
      };
      
      setChecklistAnalise(prev => ({ ...prev, [key]: resultado }));
      return resultado;
      
    } catch (error) {
      console.error("Erro ao analisar contrato:", error);
      toast.error("Erro ao analisar contrato");
      return null;
    } finally {
      setLoadingChecklist(prev => ({ ...prev, [key]: false }));
    }
  };

  const fetchBancos = async () => {
    try {
      // Usa a API /bancos que inclui os parâmetros
      const response = await axios.get(`${API}/bancos`);
      setBancos(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar bancos:", error);
    }
  };

  const fetchClientes = async () => {
    // Não é mais necessário carregar todos os clientes
    // A busca é feita por CPF individualmente
  };

  // Extrair instituições do cliente (até 2)
  const extrairInstituicoesCliente = (cliente) => {
    const instituicoes = [];
    
    // Helper para extrair nome da instituição (pode ser string ou objeto)
    const extrairNome = (inst) => {
      if (!inst) return null;
      if (typeof inst === 'string') return inst;
      if (typeof inst === 'object') return inst.nome || inst.instituicao || null;
      return null;
    };
    
    // Verificar primeira_instituicao
    const primeiraInst = extrairNome(cliente.primeira_instituicao);
    if (primeiraInst) {
      instituicoes.push(primeiraInst);
    }
    
    // Verificar segunda_instituicao
    const segundaInst = extrairNome(cliente.segunda_instituicao);
    if (segundaInst && !instituicoes.includes(segundaInst)) {
      instituicoes.push(segundaInst);
    }
    
    // Verificar dados_emprego
    const dadosEmpregoInst = extrairNome(cliente.dados_emprego?.primeira_instituicao);
    if (dadosEmpregoInst && !instituicoes.includes(dadosEmpregoInst)) {
      instituicoes.push(dadosEmpregoInst);
    }
    
    // Verificar empregos (array de instituições)
    if (cliente.empregos && Array.isArray(cliente.empregos)) {
      cliente.empregos.forEach(emp => {
        const empNome = extrairNome(emp);
        if (empNome && !instituicoes.includes(empNome)) {
          instituicoes.push(empNome);
        }
        const empInst = extrairNome(emp?.instituicao);
        if (empInst && !instituicoes.includes(empInst)) {
          instituicoes.push(empInst);
        }
      });
    }
    
    return instituicoes.slice(0, 2); // Máximo 2 instituições
  };

  // Buscar cliente principal da tela
  const buscarClientePrincipal = async () => {
    if (!cpfBuscaPrincipal.trim()) {
      toast.error("Digite o CPF para buscar");
      return;
    }
    
    setBuscandoClientePrincipal(true);
    try {
      const response = await axios.get(`${API}/clientes?search=${encodeURIComponent(cpfBuscaPrincipal.trim())}`);
      const clientesEncontrados = response.data.clientes || [];
      
      if (clientesEncontrados.length === 0) {
        toast.error("Cliente não encontrado");
        setClienteSelecionadoPrincipal(null);
        setInstituicoesCliente([]);
        setInstituicaoSelecionada("");
        setContratos([]);
        setTotais({ totalContratos: 0, totalParcelas: 0, totalSaldoDevedor: 0, totalLiberado: 0 });
      } else {
        const cpfNormalizado = cpfBuscaPrincipal.replace(/\D/g, "");
        const clienteExato = clientesEncontrados.find(c => 
          c.cpf?.replace(/\D/g, "") === cpfNormalizado
        ) || clientesEncontrados[0];
        
        setClienteSelecionadoPrincipal(clienteExato);
        
        // Extrair instituições do cliente
        const instituicoes = extrairInstituicoesCliente(clienteExato);
        setInstituicoesCliente(instituicoes);
        
        // Preencher margens automaticamente se disponíveis
        const beneficio = clienteExato.beneficio || {};
        const margemLivre = beneficio.margem_livre || beneficio.margem_disponivel || 0;
        const margemRMC = beneficio.margem_cartao_rmc || 0;
        const margemBeneficio = beneficio.margem_cartao_beneficio || 0;
        
        // Formatar valores para BR
        const formatarValor = (valor) => {
          if (!valor || valor === 0) return "0,00";
          return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };
        
        // Atualizar simulações com as margens do cliente
        setSimulacoes(prev => ({
          ...prev,
          margemLivre: { ...prev.margemLivre, margemDisponivel: formatarValor(margemLivre) },
          cartaoRMC: { ...prev.cartaoRMC, margemDisponivel: formatarValor(margemRMC) },
          cartaoBeneficio: { ...prev.cartaoBeneficio, margemDisponivel: formatarValor(margemBeneficio) }
        }));
        ativarSimulacoesImportadas(beneficio);
        
        // Se tiver apenas uma instituição, selecionar automaticamente
        if (instituicoes.length === 1) {
          setInstituicaoSelecionada(instituicoes[0]);
          toast.success(`Cliente encontrado: ${clienteExato.nome}`);
          fetchContratos(clienteExato.id, instituicoes[0]);
        } else if (instituicoes.length > 1) {
          // Se tiver mais de uma, não selecionar automaticamente
          setInstituicaoSelecionada("");
          toast.success(`Cliente encontrado: ${clienteExato.nome} - Selecione a instituição`);
          // Buscar todos os contratos do cliente inicialmente
          fetchContratos(clienteExato.id);
        } else {
          // Sem instituição cadastrada
          setInstituicaoSelecionada("");
          toast.success(`Cliente encontrado: ${clienteExato.nome}`);
          fetchContratos(clienteExato.id);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar cliente:", error);
      toast.error("Erro ao buscar cliente");
    } finally {
      setBuscandoClientePrincipal(false);
    }
  };

  // Quando mudar a instituição selecionada, recarregar contratos
  const handleInstituicaoChange = (instituicao) => {
    setInstituicaoSelecionada(instituicao);
    if (clienteSelecionadoPrincipal) {
      fetchContratos(clienteSelecionadoPrincipal.id, instituicao);
    }
  };

  const limparCliente = () => {
    setClienteSelecionadoPrincipal(null);
    setCpfBuscaPrincipal("");
    setInstituicoesCliente([]);
    setInstituicaoSelecionada("");
    setContratos([]);
    setTotais({ totalContratos: 0, totalParcelas: 0, totalSaldoDevedor: 0, totalLiberado: 0 });
  };
  
  // Buscar correspondentes
  const fetchCorrespondentes = async () => {
    try {
      const response = await axios.get(`${API}/correspondentes`);
      setCorrespondentes(response.data.correspondentes || []);
    } catch (error) {
      console.error("Erro ao carregar correspondentes:", error);
    }
  };
  
  // Buscar tabelas de comissão
  const fetchTabelasComissao = async () => {
    try {
      const response = await axios.get(`${API}/tabelas-comissao`);
      setTabelasComissao(response.data.tabelas || []);
    } catch (error) {
      console.error("Erro ao carregar tabelas de comissão:", error);
    }
  };
  
  // Filtrar bancos por instituição e tipo de operação que TEM PARÂMETROS CADASTRADOS
  const getBancosParaSimulacao = (tipoOperacao = "MARGEM_NOVA") => {
    if (!instituicaoSelecionada || bancos.length === 0) {
      return [];
    }
    
    const codigoInstituicao = instituicaoSelecionada.split(' - ')[0]?.trim();
    
    // Normaliza o tipo de operação para comparação
    const normalizarTipo = (tipo) => {
      if (!tipo) return '';
      return tipo.toString().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
    };
    
    const tipoNormalizado = normalizarTipo(tipoOperacao);
    
    // Filtra bancos que têm parâmetros para essa instituição e tipo de operação
    return bancos.filter(banco => {
      if (!banco.parametros || banco.parametros.length === 0) return false;
      
      // Verifica se tem parâmetro para a instituição e tipo de operação
      return banco.parametros.some(p => {
        const instParam = (p.instituicao || "").split(' - ')[0]?.trim();
        const mesmaInst = instParam === codigoInstituicao || 
                          p.instituicao === instituicaoSelecionada ||
                          !p.instituicao;
        const tipoParamNorm = normalizarTipo(p.tipoOperacao);
        const mesmoTipo = tipoParamNorm === tipoNormalizado || 
                          tipoParamNorm.includes(tipoNormalizado.replace('_', '')) ||
                          tipoNormalizado.includes(tipoParamNorm.replace('_', ''));
        return mesmaInst && mesmoTipo && p.ativo !== false;
      });
    });
  };
  
  // Obter parâmetros do banco para tipo de operação
  const getParametrosBanco = (bancoId, tipoOperacao = "MARGEM_NOVA") => {
    if (!bancoId || !instituicaoSelecionada) return null;
    
    const banco = bancos.find(b => b.id === bancoId);
    if (!banco?.parametros) return null;
    
    const codigoInstituicao = instituicaoSelecionada.split(' - ')[0]?.trim();
    
    // Normaliza o tipo de operação para comparação
    const normalizarTipo = (tipo) => {
      if (!tipo) return '';
      return tipo.toString().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
    };
    
    const tipoNormalizado = normalizarTipo(tipoOperacao);
    
    return banco.parametros.find(p => {
      const instParam = (p.instituicao || "").split(' - ')[0]?.trim();
      const mesmaInst = instParam === codigoInstituicao || 
                        p.instituicao === instituicaoSelecionada ||
                        !p.instituicao;
      const tipoParamNorm = normalizarTipo(p.tipoOperacao);
      const mesmoTipo = tipoParamNorm === tipoNormalizado || 
                        tipoParamNorm.includes(tipoNormalizado.replace('_', '')) ||
                        tipoNormalizado.includes(tipoParamNorm.replace('_', ''));
      return mesmaInst && mesmoTipo && p.ativo !== false;
    });
  };
  
  // Analisar critérios de simulação baseado nos parâmetros do banco
  const analisarCriteriosSimulacao = (tipoSimulacao) => {
    const simulacao = simulacoes[tipoSimulacao];
    if (!simulacao?.banco) return null;
    
    const mapaTipo = {
      'margemLivre': 'MARGEM_NOVA',
      'cartaoRMC': 'CARTAO_RMC',
      'cartaoBeneficio': 'CARTAO_BENEFICIO'
    };
    
    const parametro = getParametrosBanco(simulacao.banco, mapaTipo[tipoSimulacao]);
    if (!parametro) return null;
    
    // Calcular idade do cliente
    let idadeCliente = null;
    if (clienteSelecionadoPrincipal?.data_nascimento) {
      const nascimento = new Date(clienteSelecionadoPrincipal.data_nascimento);
      const hoje = new Date();
      idadeCliente = Math.floor((hoje - nascimento) / (365.25 * 24 * 60 * 60 * 1000));
    }
    
    const parseNum = (val) => {
      if (!val) return null;
      return parseFloat(val.toString().replace(/[^\d.,]/g, '').replace(',', '.'));
    };
    
    const criterios = [];
    
    // 1. Idade Mínima
    const idadeMin = parseNum(parametro.idadeMinima);
    if (idadeMin !== null) {
      criterios.push({
        nome: "Idade Mín.",
        valorParametro: `${idadeMin} anos`,
        valorContrato: idadeCliente ? `${idadeCliente} anos` : "N/I",
        aprovado: idadeCliente !== null && idadeCliente >= idadeMin
      });
    }
    
    // 2. Idade Máxima
    const idadeMax = parseNum(parametro.idadeMaxima);
    if (idadeMax !== null) {
      criterios.push({
        nome: "Idade Máx.",
        valorParametro: `${idadeMax} anos`,
        valorContrato: idadeCliente ? `${idadeCliente} anos` : "N/I",
        aprovado: idadeCliente !== null && idadeCliente <= idadeMax
      });
    }
    
    // 3. Parcela Mínima
    const parcelaMin = parseNum(parametro.valorParcelaMinima);
    if (parcelaMin !== null) {
      const valorParc = parseNum(simulacao.valorParc) || 0;
      criterios.push({
        nome: "Parcela Mín.",
        valorParametro: `R$ ${parcelaMin.toFixed(2)}`,
        valorContrato: `R$ ${valorParc.toFixed(2)}`,
        aprovado: valorParc >= parcelaMin
      });
    }
    
    // 4. Valor Contrato Mínimo
    const valorContratoMin = parseNum(parametro.valorContratoMinimo);
    if (valorContratoMin !== null) {
      const valorLiq = parseNum(simulacao.valorLiquido) || 0;
      criterios.push({
        nome: "Valor Mín.",
        valorParametro: `R$ ${valorContratoMin.toFixed(2)}`,
        valorContrato: `R$ ${valorLiq.toFixed(2)}`,
        aprovado: valorLiq >= valorContratoMin
      });
    }
    
    if (criterios.length === 0) return null;
    
    const todosAprovados = criterios.every(c => c.aprovado);
    
    return {
      parametro,
      criterios,
      statusGeral: todosAprovados ? "APROVADO" : "REPROVADO",
      totalCriterios: criterios.length,
      aprovados: criterios.filter(c => c.aprovado).length,
      reprovados: criterios.filter(c => !c.aprovado).length
    };
  };
  
  // Buscar tabelas filtradas por banco, instituição e tipo de operação
  const getTabelasParaBanco = (bancoId, tipoOperacao = "MARGEM_NOVA") => {
    if (!bancoId || !instituicaoSelecionada || tabelasComissao.length === 0) {
      return [];
    }
    
    const codigoInstituicao = instituicaoSelecionada.split(' - ')[0]?.trim();
    
    return tabelasComissao.filter(tabela => {
      const codigoTabelaInst = (tabela.instituicao || "").split(' - ')[0]?.trim();
      const mesmaInst = codigoTabelaInst === codigoInstituicao || tabela.instituicao === instituicaoSelecionada;
      const mesmoBanco = tabela.banco_id === bancoId;
      
      return mesmaInst && mesmoBanco;
    });
  };
  
  // Buscar correspondentes de um banco específico
  const getCorrespondentesDoBanco = (bancoId) => {
    if (!bancoId || tabelasComissao.length === 0) {
      return [];
    }
    
    const correspondenteIds = new Set();
    tabelasComissao.forEach(t => {
      if (t.banco_id === bancoId && t.correspondente_id) {
        correspondenteIds.add(t.correspondente_id);
      }
    });
    
    return correspondentes.filter(c => correspondenteIds.has(c.id));
  };
  
  // Atualizar campo de simulação
  const updateSimulacao = (tipo, campo, valor) => {
    setSimulacoes(prev => ({
      ...prev,
      [tipo]: { ...prev[tipo], [campo]: valor }
    }));
  };

  useEffect(() => {
    // Não carrega contratos automaticamente - precisa selecionar cliente primeiro
    fetchBancos();
    fetchCorrespondentes();
    fetchTabelasComissao();
    
    // Verificar se veio cliente_id na URL
    const clienteIdParam = searchParams.get("cliente_id");
    if (clienteIdParam) {
      carregarClientePorId(clienteIdParam);
    }
  }, []);

  // Função para carregar cliente por ID (vindo da URL)
  const carregarClientePorId = async (clienteId) => {
    setBuscandoClientePrincipal(true);
    try {
      const response = await axios.get(`${API}/clientes/${clienteId}`);
      const cliente = response.data;
      
      if (cliente) {
        setClienteSelecionadoPrincipal(cliente);
        setCpfBuscaPrincipal(cliente.cpf || "");
        
        // Extrair instituições do cliente
        const instituicoes = extrairInstituicoesCliente(cliente);
        setInstituicoesCliente(instituicoes);
        
        // Preencher margens automaticamente se disponíveis
        const beneficio = cliente.beneficio || {};
        const margemLivre = beneficio.margem_livre || beneficio.margem_disponivel || 0;
        const margemRMC = beneficio.margem_cartao_rmc || 0;
        const margemBeneficio = beneficio.margem_cartao_beneficio || 0;
        
        // Formatar valores para BR
        const formatarValor = (valor) => {
          if (!valor || valor === 0) return "0,00";
          return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };
        
        // Atualizar simulações com as margens do cliente
        setSimulacoes(prev => ({
          ...prev,
          margemLivre: { ...prev.margemLivre, margemDisponivel: formatarValor(margemLivre) },
          cartaoRMC: { ...prev.cartaoRMC, margemDisponivel: formatarValor(margemRMC) },
          cartaoBeneficio: { ...prev.cartaoBeneficio, margemDisponivel: formatarValor(margemBeneficio) }
        }));
        ativarSimulacoesImportadas(beneficio);
        
        if (instituicoes.length > 0) {
          setInstituicaoSelecionada(instituicoes[0]);
          toast.success(`Cliente: ${cliente.nome}`);
          fetchContratos(cliente.id, instituicoes[0]);
        } else {
          setInstituicaoSelecionada("");
          toast.success(`Cliente: ${cliente.nome}`);
          fetchContratos(cliente.id);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar cliente:", error);
      toast.error("Erro ao carregar cliente");
    } finally {
      setBuscandoClientePrincipal(false);
    }
  };

  // useEffect para recalcular o saldo devedor automaticamente quando mudar o percentual de desconto
  // ou qualquer campo relevante para o cálculo
  useEffect(() => {
    if (!dialogOpen) return; // Só recalcula quando o modal estiver aberto
    
    const { valor_parcela, quantidade_parcelas, parcela_atual, taxa_juros } = formData;
    
    // Só recalcula se não tiver taxa de juros (ou for 0)
    if (!valor_parcela || !quantidade_parcelas || !parcela_atual) return;
    if (taxa_juros && parseFloat(taxa_juros) > 0) return;
    
    const parcelasRestantes = calcularParcelasRestantes(quantidade_parcelas, parcela_atual);
    if (parcelasRestantes <= 0) return;
    
    const novoSaldoDevedor = calcularSaldoDevedor(
      valor_parcela, 
      parcelasRestantes, 
      taxa_juros, 
      percentualDesconto, 
      quantidade_parcelas, 
      parcela_atual
    );
    
    // Só atualiza se o valor for diferente para evitar loop infinito
    if (novoSaldoDevedor !== formData.saldo_devedor) {
      setFormData(prev => ({
        ...prev,
        saldo_devedor: novoSaldoDevedor
      }));
    }
  }, [percentualDesconto, formData.valor_parcela, formData.quantidade_parcelas, formData.parcela_atual, formData.taxa_juros, dialogOpen]);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
    fetchContratos();
  };

  const buscarClientePorCPF = async () => {
    if (!cpfBusca.trim()) {
      toast.error("Digite o CPF para buscar");
      return;
    }
    
    setBuscandoCliente(true);
    try {
      const response = await axios.get(`${API}/clientes?search=${encodeURIComponent(cpfBusca.trim())}`);
      const clientesEncontrados = response.data.clientes || [];
      
      const preencherDadosCliente = (cliente) => {
        setClienteSelecionado(cliente);
        
        // Extrair instituição do cadastro do cliente (pode ser string ou objeto)
        const extrairInstNome = (inst) => {
          if (!inst) return "";
          if (typeof inst === 'string') return inst;
          if (typeof inst === 'object') return inst.nome || inst.instituicao || "";
          return "";
        };
        
        let instituicaoCliente = "";
        if (cliente.primeira_instituicao) {
          instituicaoCliente = extrairInstNome(cliente.primeira_instituicao);
        } else if (cliente.dados_emprego?.primeira_instituicao) {
          instituicaoCliente = extrairInstNome(cliente.dados_emprego.primeira_instituicao);
        } else if (cliente.emprego?.primeira_instituicao) {
          instituicaoCliente = extrairInstNome(cliente.emprego.primeira_instituicao);
        }
        
        setFormData({
          ...formData, 
          cliente_id: cliente.id,
          instituicao: instituicaoCliente
        });
        toast.success(`Cliente encontrado: ${cliente.nome}`);
      };
      
      if (clientesEncontrados.length === 0) {
        toast.error("Cliente não encontrado");
        setClienteSelecionado(null);
        setFormData({...formData, cliente_id: "", instituicao: ""});
      } else if (clientesEncontrados.length === 1) {
        preencherDadosCliente(clientesEncontrados[0]);
      } else {
        // Se encontrou mais de um, pegar o primeiro que bate com o CPF exato
        const cpfNormalizado = cpfBusca.replace(/\D/g, "");
        const clienteExato = clientesEncontrados.find(c => 
          c.cpf.replace(/\D/g, "") === cpfNormalizado
        );
        
        if (clienteExato) {
          preencherDadosCliente(clienteExato);
        } else {
          preencherDadosCliente(clientesEncontrados[0]);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar cliente:", error);
      toast.error("Erro ao buscar cliente");
    } finally {
      setBuscandoCliente(false);
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: "",
      numero_contrato: "",
      tipo: "EXTERNO",
      tipo_operacao: "",
      banco_codigo: "",
      banco_nome: "",
      instituicao: "",
      valor_parcela: "",
      quantidade_parcelas: "",
      parcela_atual: "",
      parcelas_restantes: "",
      taxa_juros: "",
      valor_liberado: "",
      saldo_devedor: "",
      data_inicio: "",
      data_fim: "",
      data_averbacao: "",
      situacao: "ATIVO",
      observacoes: "",
      margem_disponivel: "",
      comissao_calculada: ""
    });
    setEditingContrato(null);
    setCpfBusca("");
    setClienteSelecionado(null);
  };

  const handleBancoChange = (codigo) => {
    const banco = bancos.find(b => b.codigo === codigo);
    setFormData({
      ...formData,
      banco_codigo: codigo,
      banco_nome: banco?.nome || ""
    });
  };

  // Função para calcular Parcelas Restantes
  const calcularParcelasRestantes = (totalParcelas, parcelasPagas) => {
    const total = parseInt(totalParcelas) || 0;
    const pagas = parseInt(parcelasPagas) || 0;
    return Math.max(0, total - pagas);
  };

  // Função para adicionar/subtrair meses de uma data
  const adicionarMeses = (dataString, meses) => {
    if (!dataString) return "";
    const data = new Date(dataString);
    if (isNaN(data.getTime())) return "";
    
    data.setMonth(data.getMonth() + meses);
    
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    
    return `${ano}-${mes}-${dia}`;
  };

  // Função para calcular Data Início baseado em Data de Referência e Parcelas Pagas
  // Data Início = Data de Referência - (QPP - 1) meses
  const calcularDataInicio = (dataReferencia, parcelasPagas) => {
    if (!dataReferencia) return "";
    const pagas = parseInt(parcelasPagas) || 0;
    if (pagas <= 0) return dataReferencia; // Se não há parcelas pagas, a referência é o início
    
    // Retroceder (pagas - 1) meses
    const mesesRetroceder = pagas - 1;
    return adicionarMeses(dataReferencia, -mesesRetroceder);
  };

  // Função para calcular Data Fim baseado em Data Início e Total de Parcelas
  // Data Fim = Data Início + (QTP - 1) meses
  const calcularDataFim = (dataInicio, totalParcelas) => {
    if (!dataInicio) return "";
    const total = parseInt(totalParcelas) || 0;
    if (total <= 0) return "";
    
    // Adicionar (total - 1) meses
    const mesesAdicionar = total - 1;
    return adicionarMeses(dataInicio, mesesAdicionar);
  };

  // Função para calcular Saldo Devedor
  // Regra 3: Com taxa - usa método Price: S = PMT × [(1 - (1 + i)^-n) / i]
  // Regra 3.1: Sem taxa - usa cálculo simples com desconto baseado na faixa
  const calcularSaldoDevedor = (valorParcela, parcelasRestantes, taxaJuros, desconto = 20, totalParcelas = 96, parcelasPagas = 0) => {
    const pmt = parseFloat(valorParcela) || 0;
    const n = parseInt(parcelasRestantes) || 0;
    const taxa = parseFloat(taxaJuros) || 0;
    const total = parseInt(totalParcelas) || 96;
    const pagas = parseInt(parcelasPagas) || 0;
    
    // Se não tiver valor de parcela ou parcelas restantes, não calcula
    if (pmt <= 0 || n <= 0) return "";
    
    let saldoDevedor;
    
    if (taxa > 0) {
      // Regra 3: Com taxa - usa método Price
      // Converter taxa de porcentagem para decimal (1.5% -> 0.015)
      const i = taxa / 100;
      
      // Calcular (1 + i)^-n
      const fator = Math.pow(1 + i, -n);
      
      // Calcular [1 - (1 + i)^-n] / i
      const coeficiente = (1 - fator) / i;
      
      // Calcular Saldo Devedor
      saldoDevedor = pmt * coeficiente;
    } else {
      // Regra 3.1: Sem taxa - usa cálculo baseado no VTC e faixa de parcelas pagas
      // VTC = Valor da Parcela × Total de Parcelas
      const vtc = pmt * total;
      
      // Identificar a faixa e usar o máximo da faixa para cálculo
      let maxFaixa = 10;
      if (pagas <= 10) maxFaixa = 10;
      else if (pagas <= 20) maxFaixa = 20;
      else if (pagas <= 30) maxFaixa = 30;
      else if (pagas <= 40) maxFaixa = 40;
      else if (pagas <= 60) maxFaixa = 60;
      else if (pagas <= 70) maxFaixa = 70;
      else maxFaixa = 100;
      
      // Parcelas restantes baseado no máximo da faixa
      const restantesFaixa = total - maxFaixa;
      
      // Saldo Inicial = VTC × (restantes / total)
      const saldoInicial = vtc * (restantesFaixa / total);
      
      // Aplicar desconto selecionado
      const percentDesc = parseFloat(desconto) / 100;
      saldoDevedor = saldoInicial * (1 - percentDesc);
    }
    
    // Retornar com 2 casas decimais
    return saldoDevedor.toFixed(2);
  };

  // Função para recalcular todos os campos baseado nos dados disponíveis
  const recalcularCampos = (novosDados) => {
    const totalParcelas = novosDados.quantidade_parcelas || formData.quantidade_parcelas;
    const parcelasPagas = novosDados.parcela_atual || formData.parcela_atual;
    const dataReferencia = novosDados.data_averbacao || formData.data_averbacao;
    const valorParcela = novosDados.valor_parcela || formData.valor_parcela;
    const taxaJuros = novosDados.taxa_juros !== undefined ? novosDados.taxa_juros : formData.taxa_juros;
    
    // Calcular parcelas restantes
    const parcelasRestantes = calcularParcelasRestantes(totalParcelas, parcelasPagas);
    
    // Calcular data de início (se temos data de referência e parcelas pagas)
    let dataInicio = novosDados.data_inicio || formData.data_inicio;
    if (dataReferencia && parcelasPagas) {
      dataInicio = calcularDataInicio(dataReferencia, parcelasPagas);
    }
    
    // Calcular data de fim (se temos data de início e total de parcelas)
    let dataFim = "";
    if (dataInicio && totalParcelas) {
      dataFim = calcularDataFim(dataInicio, totalParcelas);
    }
    
    // Calcular saldo devedor
    // Regra 3: Com taxa - usa método Price
    // Regra 3.1: Sem taxa - usa cálculo simples com desconto selecionado
    const desconto = novosDados.desconto || percentualDesconto;
    let saldoDevedor = "";
    if (valorParcela && parcelasRestantes > 0) {
      saldoDevedor = calcularSaldoDevedor(valorParcela, parcelasRestantes, taxaJuros, desconto, totalParcelas, parcelasPagas);
    }
    
    return {
      parcelas_restantes: parcelasRestantes.toString(),
      data_inicio: dataInicio,
      data_fim: dataFim,
      saldo_devedor: saldoDevedor
    };
  };

  // Handler para mudança de percentual de desconto
  const handleDescontoChange = (value) => {
    setPercentualDesconto(value);
    
    // Recalcular com o novo desconto usando valores atuais do formData
    const totalParcelas = formData.quantidade_parcelas;
    const parcelasPagas = formData.parcela_atual;
    const valorParcela = formData.valor_parcela;
    const taxaJuros = formData.taxa_juros;
    const parcelasRestantes = calcularParcelasRestantes(totalParcelas, parcelasPagas);
    
    let saldoDevedor = "";
    if (valorParcela && parcelasRestantes > 0) {
      saldoDevedor = calcularSaldoDevedor(valorParcela, parcelasRestantes, taxaJuros, value, totalParcelas, parcelasPagas);
    }
    
    setFormData(prev => ({
      ...prev,
      saldo_devedor: saldoDevedor
    }));
  };

  // Handler para Qtd. Total Parcelas
  const handleTotalParcelasChange = (value) => {
    const calculados = recalcularCampos({ quantidade_parcelas: value });
    
    setFormData({
      ...formData,
      quantidade_parcelas: value,
      ...calculados
    });
  };

  // Handler para Qtd. Pagas
  const handleParcelasPagasChange = (value) => {
    const calculados = recalcularCampos({ parcela_atual: value });
    
    setFormData({
      ...formData,
      parcela_atual: value,
      ...calculados
    });
  };

  // Handler para Data de Referência
  const handleDataReferenciaChange = (value) => {
    const calculados = recalcularCampos({ data_averbacao: value });
    
    setFormData({
      ...formData,
      data_averbacao: value,
      ...calculados
    });
  };

  // Handler para Valor da Parcela
  const handleValorParcelaChange = (value) => {
    const calculados = recalcularCampos({ valor_parcela: value });
    
    setFormData({
      ...formData,
      valor_parcela: value,
      ...calculados
    });
  };

  // Handler para Taxa de Juros
  const handleTaxaJurosChange = (value) => {
    const calculados = recalcularCampos({ taxa_juros: value });
    
    setFormData({
      ...formData,
      taxa_juros: value,
      ...calculados
    });
  };

  // Handler para Data Início (manual - recalcula apenas data fim)
  const handleDataInicioChange = (value) => {
    const totalParcelas = formData.quantidade_parcelas;
    const dataFim = calcularDataFim(value, totalParcelas);
    
    setFormData({
      ...formData,
      data_inicio: value,
      data_fim: dataFim || formData.data_fim
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cliente_id || !formData.numero_contrato) {
      toast.error("Cliente e número do contrato são obrigatórios");
      return;
    }

    try {
      const payload = {
        ...formData,
        // Mapear data_averbacao para data_referencia (nome do campo no backend)
        data_referencia: formData.data_averbacao,
        valor_parcela: formData.valor_parcela ? parseFloat(formData.valor_parcela) : null,
        quantidade_parcelas: formData.quantidade_parcelas ? parseInt(formData.quantidade_parcelas) : null,
        parcela_atual: formData.parcela_atual ? parseInt(formData.parcela_atual) : null,
        parcelas_restantes: formData.parcelas_restantes ? parseInt(formData.parcelas_restantes) : null,
        taxa_juros: formData.taxa_juros ? parseFloat(formData.taxa_juros) : null,
        valor_liberado: formData.valor_liberado ? parseFloat(formData.valor_liberado) : null,
        saldo_devedor: formData.saldo_devedor ? parseFloat(formData.saldo_devedor) : null,
        margem_disponivel: formData.margem_disponivel ? parseFloat(formData.margem_disponivel) : null,
        comissao_calculada: formData.comissao_calculada ? parseFloat(formData.comissao_calculada) : null,
      };
      
      // Remover data_averbacao do payload (já foi mapeado para data_referencia)
      delete payload.data_averbacao;

      if (editingContrato) {
        await axios.put(`${API}/contratos/${editingContrato.id}`, payload);
        toast.success("Contrato atualizado com sucesso!");
      } else {
        await axios.post(`${API}/contratos`, payload);
        toast.success("Contrato criado com sucesso!");
      }

      setDialogOpen(false);
      resetForm();
      fetchContratos();
    } catch (error) {
      console.error("Erro ao salvar contrato:", error);
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg || e).join(', '));
      } else {
        toast.error("Erro ao salvar contrato");
      }
    }
  };

  const handleEdit = async (contrato) => {
    const totalParcelas = contrato.quantidade_parcelas?.toString() || "";
    const parcelasPagas = contrato.parcela_atual?.toString() || "";
    const valorParcela = contrato.valor_parcela?.toString() || "";
    const taxaJuros = contrato.taxa_juros?.toString() || "";
    
    // Sempre recalcular o saldo devedor automaticamente
    let saldoDevedor = "";
    if (valorParcela && totalParcelas && parcelasPagas) {
      const parcelasRestantes = calcularParcelasRestantes(totalParcelas, parcelasPagas);
      if (parcelasRestantes > 0) {
        saldoDevedor = calcularSaldoDevedor(valorParcela, parcelasRestantes, taxaJuros, percentualDesconto, totalParcelas, parcelasPagas);
      }
    }
    
    setFormData({
      cliente_id: contrato.cliente_id || "",
      numero_contrato: contrato.numero_contrato || "",
      tipo: contrato.tipo || "EXTERNO",
      tipo_operacao: contrato.tipo_operacao || "",
      banco_codigo: contrato.banco_codigo || "",
      banco_nome: contrato.banco_nome || "",
      instituicao: contrato.instituicao || "",
      valor_parcela: valorParcela,
      quantidade_parcelas: totalParcelas,
      parcela_atual: parcelasPagas,
      parcelas_restantes: contrato.parcelas_restantes?.toString() || "",
      taxa_juros: taxaJuros,
      valor_liberado: contrato.valor_liberado?.toString() || "",
      saldo_devedor: saldoDevedor,
      data_inicio: contrato.data_inicio || "",
      data_fim: contrato.data_fim || "",
      data_averbacao: contrato.data_referencia || contrato.data_averbacao || "",
      situacao: contrato.situacao || "ATIVO",
      observacoes: contrato.observacoes || "",
      margem_disponivel: contrato.margem_disponivel?.toString() || "",
      comissao_calculada: contrato.comissao_calculada?.toString() || ""
    });
    setEditingContrato(contrato);
    
    // Usar nome do cliente que já vem no contrato
    if (contrato.cliente_nome) {
      setCpfBusca(contrato.cliente_cpf || "");
      setClienteSelecionado({
        id: contrato.cliente_id,
        nome: contrato.cliente_nome,
        cpf: contrato.cliente_cpf,
        primeira_instituicao: contrato.instituicao
      });
    }
    
    setDialogOpen(true);
  };

  const handleDelete = async (id, tipo) => {
    if (!confirm("Tem certeza que deseja excluir este contrato?")) return;
    
    // Para contratos INTERNOS: apenas ocultar da tela (não pode excluir do banco)
    if (tipo === "INTERNO") {
      setContratosOcultos(prev => ({ ...prev, [id]: true }));
      toast.success("Contrato excluído com sucesso!");
      return;
    }
    
    // Para contratos EXTERNOS: excluir normalmente do banco
    try {
      await axios.delete(`${API}/contratos/${id}`);
      toast.success("Contrato excluído com sucesso!");
      fetchContratos();
    } catch (error) {
      console.error("Erro ao excluir contrato:", error);
      toast.error("Erro ao excluir contrato");
    }
  };

  const getClienteNome = (contrato) => {
    return contrato.cliente_nome || "-";
  };

  // ==================== FUNÇÕES DE IMPORTAÇÃO DE EXTRATO ====================
  
  const handleArquivoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error("Apenas arquivos PDF são aceitos");
        return;
      }
      setArquivoSelecionado(file);
    }
  };

  const processarExtrato = async () => {
    if (!arquivoSelecionado) {
      toast.error("Selecione um arquivo PDF");
      return;
    }

    setImportando(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('arquivo', arquivoSelecionado);

      const response = await axios.post(`${API}/extratos/processar`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setDadosExtraidos(response.data.dados);
        setEtapaImportacao('preview');
        toast.success("Extrato processado! Verifique os dados extraídos.");
      } else {
        toast.error("Erro ao processar extrato");
      }
    } catch (error) {
      console.error("Erro ao processar extrato:", error);
      const msg = error.response?.data?.detail || "Erro ao processar extrato";
      toast.error(msg);
    } finally {
      setImportando(false);
    }
  };

  const confirmarImportacao = async () => {
    if (!dadosExtraidos) {
      toast.error("Nenhum dado para importar");
      return;
    }

    setImportando(true);
    try {
      // Obter empresa_id do localStorage para multi-tenancy
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      const usuarioAtual = JSON.parse(localStorage.getItem("usuario") || "null");
      
      // Incluir o tipo de extrato selecionado nos dados
      const dadosComTipo = {
        ...dadosExtraidos,
        tipo_extrato: tipoExtrato,
        empresa_id: empresaAtual?.id || null,
        atendente_id: usuarioAtual?.id || null
      };
      const response = await axios.post(`${API}/extratos/importar`, dadosComTipo);

      if (response.data.success) {
        setEtapaImportacao('success');
        toast.success(response.data.mensagem);
        
        // Se criou cliente novo ou já existe, buscar automaticamente
        if (response.data.cliente_cpf) {
          setCpfBuscaPrincipal(response.data.cliente_cpf);
          setTimeout(() => {
            buscarClientePrincipal();
          }, 500);
        }
      }
    } catch (error) {
      console.error("Erro ao importar:", error);
      const msg = error.response?.data?.detail || "Erro ao importar dados";
      toast.error(msg);
    } finally {
      setImportando(false);
    }
  };

  const resetImportacao = () => {
    setArquivoSelecionado(null);
    setDadosExtraidos(null);
    setEtapaImportacao('upload');
    setTipoExtrato('SIAPE');
    setImportDialogOpen(false);
  };

  // Função para toggle simulação
  const toggleSimulacao = (tipo) => {
    setSimulacoes(prev => ({
      ...prev,
      [tipo]: {
        ...prev[tipo],
        ativo: !prev[tipo].ativo
      }
    }));
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header com busca de cliente */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Landmark className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Monitoramento Contrato</h1>
            <p className="text-xs text-muted-foreground">Análise de contratos, Internos e Externos</p>
          </div>
        </div>
        
        {/* Busca CPF e Instituição no header */}
        <div className="flex items-center gap-2">
          <Label className="text-xs font-semibold whitespace-nowrap">CPF:</Label>
          <Input
            value={cpfBuscaPrincipal}
            onChange={(e) => setCpfBuscaPrincipal(e.target.value)}
            placeholder="Digite o CPF"
            className="w-36 h-8 text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                buscarClientePrincipal();
              }
            }}
          />
          <Label className="text-xs font-semibold whitespace-nowrap">Instituição:</Label>
          <Select 
            value={instituicaoSelecionada} 
            onValueChange={handleInstituicaoChange}
            disabled={instituicoesCliente.length === 0}
          >
            <SelectTrigger className="w-52 h-8 text-xs">
              <SelectValue placeholder={instituicoesCliente.length === 0 ? "Busque um CPF" : "Selecione"} />
            </SelectTrigger>
            <SelectContent>
              {instituicoesCliente.map((inst, idx) => (
                <SelectItem key={idx} value={inst}>{inst}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={buscarClientePrincipal}
            disabled={buscandoClientePrincipal}
            size="sm"
            className="h-8"
          >
            {buscandoClientePrincipal ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
          {clienteSelecionadoPrincipal && (
            <Button variant="outline" onClick={limparCliente} size="sm" className="h-8">
              <X className="h-4 w-4" />
            </Button>
          )}
          
          {/* Botão Importar Extrato */}
          <Dialog open={importDialogOpen} onOpenChange={(open) => {
            if (!open) resetImportacao();
            setImportDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-2 ml-4 border-green-500 text-green-600 hover:bg-green-50">
                <FileUp className="h-4 w-4" />
                Importar Extrato
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileUp className="h-5 w-5 text-green-600" />
                  Importar Extrato de Consignação
                </DialogTitle>
                <DialogDescription>
                  Faça upload de um PDF de extrato para extrair automaticamente os dados do cliente e contratos.
                </DialogDescription>
              </DialogHeader>

              {etapaImportacao === 'upload' && (
                <div className="space-y-6 py-4">
                  {/* Seleção do Tipo de Extrato */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Tipo de Extrato</Label>
                    <Select value={tipoExtrato} onValueChange={setTipoExtrato}>
                      <SelectTrigger className="w-full" data-testid="tipo-extrato-select">
                        <SelectValue placeholder="Selecione o tipo de extrato" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SIAPE">SIAPE - Servidor Federal</SelectItem>
                        <SelectItem value="SIAPE_VANGUARD">SIAPE Vanguard</SelectItem>
                        <SelectItem value="INSS">INSS - Previdência Social</SelectItem>
                        <SelectItem value="VANGUARD">INSS Vanguard</SelectItem>
                        <SelectItem value="EXERCITO">Exército</SelectItem>
                        <SelectItem value="MARINHA">Marinha</SelectItem>
                        <SelectItem value="AERONAUTICA">Aeronáutica</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {tipoExtrato === 'SIAPE' && 'Extrato de servidor federal civil (SIAPE tradicional)'}
                      {tipoExtrato === 'SIAPE_VANGUARD' && 'Extrato SIAPE gerado pela plataforma Vanguard'}
                      {tipoExtrato === 'INSS' && 'Extrato do INSS tradicional'}
                      {tipoExtrato === 'VANGUARD' && 'Extrato INSS gerado pela plataforma Vanguard'}
                      {tipoExtrato === 'EXERCITO' && 'Extrato de militar do Exército'}
                      {tipoExtrato === 'MARINHA' && 'Extrato de militar da Marinha'}
                      {tipoExtrato === 'AERONAUTICA' && 'Extrato de militar da Aeronáutica'}
                    </p>
                  </div>

                  {/* Área de Upload */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-green-400 transition-colors">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleArquivoChange}
                      className="hidden"
                      id="extrato-upload"
                    />
                    <label htmlFor="extrato-upload" className="cursor-pointer">
                      <Upload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                      <p className="text-lg font-medium text-slate-700">
                        {arquivoSelecionado ? arquivoSelecionado.name : "Clique para selecionar o PDF"}
                      </p>
                      <p className="text-sm text-slate-500 mt-2">
                        Apenas arquivos PDF são aceitos
                      </p>
                    </label>
                  </div>

                  {arquivoSelecionado && (
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-green-600" />
                        <div>
                          <p className="font-medium text-green-700">{arquivoSelecionado.name}</p>
                          <p className="text-sm text-green-600">{(arquivoSelecionado.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setArquivoSelecionado(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  <Button 
                    onClick={processarExtrato} 
                    disabled={!arquivoSelecionado || importando}
                    className="w-full gap-2"
                    size="lg"
                  >
                    {importando ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Processando com IA...
                      </>
                    ) : (
                      <>
                        <Search className="h-5 w-5" />
                        Extrair Dados do PDF
                      </>
                    )}
                  </Button>
                </div>
              )}

              {etapaImportacao === 'preview' && dadosExtraidos && (
                <div className="space-y-6 py-4">
                  {/* Dados do Cliente */}
                  <Card className="border-blue-200 bg-blue-50/50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        Dados do Cliente
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-slate-500">Nome</Label>
                        <p className="font-medium">{dadosExtraidos.cliente?.nome || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">CPF</Label>
                        <p className="font-medium">{dadosExtraidos.cliente?.cpf || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Órgão/Instituição</Label>
                        <p className="font-medium">{dadosExtraidos.cliente?.orgao || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Matrícula/Benefício</Label>
                        <p className="font-medium">{dadosExtraidos.cliente?.matricula || dadosExtraidos.cliente?.numero_beneficio || '-'}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Margem Consignável */}
                  {dadosExtraidos.margem && (
                    <Card className="border-purple-200 bg-purple-50/50">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-purple-600" />
                          Margem Consignável
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <Label className="text-xs text-slate-500">Líquida Compulsória</Label>
                          <p className="font-medium text-green-600">{formatCurrency(dadosExtraidos.margem.liquida_compulsoria)}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Líquida Facultativa</Label>
                          <p className="font-medium text-green-600">{formatCurrency(dadosExtraidos.margem.liquida_facultativa)}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Líquida Cartão</Label>
                          <p className="font-medium text-green-600">{formatCurrency(dadosExtraidos.margem.liquida_cartao)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Contratos Extraídos */}
                  <Card className="border-orange-200 bg-orange-50/50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="h-4 w-4 text-orange-600" />
                        Contratos Encontrados ({dadosExtraidos.contratos?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-64 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Banco</TableHead>
                              <TableHead className="text-xs">Contrato</TableHead>
                              <TableHead className="text-xs">Parcela</TableHead>
                              <TableHead className="text-xs">Qtd/Rest</TableHead>
                              <TableHead className="text-xs">Tipo</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {dadosExtraidos.contratos?.map((contrato, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="text-xs font-medium">{contrato.banco_nome}</TableCell>
                                <TableCell className="text-xs">{contrato.numero_contrato}</TableCell>
                                <TableCell className="text-xs">{formatCurrency(contrato.valor_parcela)}</TableCell>
                                <TableCell className="text-xs">{contrato.quantidade_parcelas}/{contrato.parcelas_restantes || '?'}</TableCell>
                                <TableCell className="text-xs">{contrato.tipo_operacao || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setEtapaImportacao('upload')}
                      className="flex-1"
                    >
                      Voltar
                    </Button>
                    <Button 
                      onClick={confirmarImportacao} 
                      disabled={importando}
                      className="flex-1 bg-green-600 hover:bg-green-700 gap-2"
                    >
                      {importando ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Importando...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4" />
                          Confirmar Importação
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {etapaImportacao === 'success' && (
                <div className="space-y-6 py-8 text-center">
                  <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-green-700">Importação Concluída!</h3>
                    <p className="text-slate-600 mt-2">Os dados foram importados com sucesso.</p>
                  </div>
                  <Button onClick={resetImportacao} className="gap-2">
                    <FileUp className="h-4 w-4" />
                    Importar Outro Extrato
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Dados do Cliente Selecionado */}
      {clienteSelecionadoPrincipal && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex shrink-0 items-center gap-4 flex-wrap xl:max-w-sm">
                <div>
                  <CardTitle className="text-xl text-primary">{clienteSelecionadoPrincipal.nome}</CardTitle>
                  <CardDescription>CPF: {formatCPF(clienteSelecionadoPrincipal.cpf)}</CardDescription>
                </div>
                
                {/* Restrições do Cliente */}
                {(clienteSelecionadoPrincipal.acoes_operacionais?.bancosRestricaoInterna?.length > 0 || 
                  clienteSelecionadoPrincipal.acoes_operacionais?.motivosRestricaoGeral?.length > 0) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Bancos com Restrição Interna */}
                    {clienteSelecionadoPrincipal.acoes_operacionais?.bancosRestricaoInterna?.map((restricao, idx) => (
                      <div 
                        key={`banco-${idx}`} 
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-100 border border-red-300 text-red-800 text-xs"
                      >
                        <Ban className="h-3.5 w-3.5" />
                        <span className="font-semibold">{restricao.banco_nome || restricao.banco_codigo}</span>
                        <span className="text-red-600">Restrição Interna</span>
                      </div>
                    ))}
                    
                    {/* Motivos de Restrição Geral */}
                    {clienteSelecionadoPrincipal.acoes_operacionais?.motivosRestricaoGeral?.map((restricao, idx) => (
                      <div 
                        key={`motivo-${idx}`} 
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-orange-100 border border-orange-300 text-orange-800 text-xs"
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                        <span className="font-semibold">{restricao.motivo}</span>
                        <span className="text-orange-600">Restrição Geral</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <JornadaCreditoCliente cliente={clienteSelecionadoPrincipal} />
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="gap-2" onClick={() => {
                    // Pré-preencher com dados do cliente principal
                    setClienteSelecionado(clienteSelecionadoPrincipal);
                    // Usar a instituição SELECIONADA no dropdown, não a primeira do cadastro
                    setFormData({
                      ...formData,
                      cliente_id: clienteSelecionadoPrincipal.id,
                      instituicao: instituicaoSelecionada || ""
                    });
                  }}>
                    <Plus className="h-4 w-4" />
                    Novo Contrato
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                  {/* Header com nome do cliente */}
                  {clienteSelecionado && (
                    <div className="flex items-center justify-between bg-slate-100 -mx-6 -mt-6 px-6 py-3 mb-4">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-slate-600" />
                        <span className="font-semibold text-slate-700">{clienteSelecionado.nome}</span>
                      </div>
                    </div>
                  )}
                  
                  <DialogHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-8 bg-green-500 rounded"></div>
                      <DialogTitle className="text-xl">
                        {editingContrato ? "Editar Contrato Externo" : "Cadastrar Contrato Externo do Cliente"}
                      </DialogTitle>
                    </div>
                  </DialogHeader>

                  {/* Banner CONTRATO EXTERNO */}
            <div className="bg-red-600 text-white font-bold py-2 px-4 rounded -mx-2 mt-2">
              CONTRATO EXTERNO
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Linha 1: CPF e Busca */}
              {!clienteSelecionado && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label className="text-sm font-semibold">CPF do Cliente *</Label>
                    <div className="flex gap-2">
                      <Input
                        value={cpfBusca}
                        onChange={(e) => setCpfBusca(e.target.value)}
                        placeholder="Digite o CPF"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            buscarClientePorCPF();
                          }
                        }}
                      />
                      <Button 
                        type="button" 
                        onClick={buscarClientePorCPF}
                        disabled={buscandoCliente}
                        className="shrink-0"
                      >
                        {buscandoCliente ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Mostrar dados do cliente se encontrado */}
              {clienteSelecionado && (
                <>
                  {/* Linha 1: Banco e Instituição */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label className="text-sm font-semibold">Banco:</Label>
                      <Select value={formData.banco_codigo} onValueChange={handleBancoChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o banco" />
                        </SelectTrigger>
                        <SelectContent>
                          {bancos.map((banco) => (
                            <SelectItem key={banco.codigo} value={banco.codigo}>
                              {banco.codigo} - {banco.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Instituição:</Label>
                      <div className="h-10 px-3 py-2 border rounded-md bg-red-50 border-red-200 text-red-700 font-medium flex items-center">
                        {formData.instituicao || "Não informada"}
                      </div>
                    </div>
                  </div>

                  {/* Linha 2: Parcelas */}
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label className="text-sm font-semibold">Qtd. Total Parcelas:</Label>
                      <Input
                        type="number"
                        value={formData.quantidade_parcelas}
                        onChange={(e) => handleTotalParcelasChange(e.target.value)}
                        placeholder="96"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Qtd.Pagas:</Label>
                      <Input
                        type="number"
                        value={formData.parcela_atual}
                        onChange={(e) => handleParcelasPagasChange(e.target.value)}
                        placeholder="Ex: 12"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Parc.Restantes:</Label>
                      <Input
                        type="number"
                        value={formData.parcelas_restantes}
                        readOnly
                        className="bg-gray-100"
                        placeholder="Calculado automaticamente"
                      />
                    </div>
                  </div>

                  {/* Linha 3: Datas */}
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label className="text-sm font-semibold">Data de Referência:</Label>
                      <Input
                        type="date"
                        value={formData.data_averbacao}
                        onChange={(e) => handleDataReferenciaChange(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Data Início Contrato:</Label>
                      <Input
                        type="date"
                        value={formData.data_inicio}
                        readOnly
                        className="bg-gray-100"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Data Fim Contrato:</Label>
                      <Input
                        type="date"
                        value={formData.data_fim}
                        readOnly
                        className="bg-gray-100"
                      />
                    </div>
                  </div>

                  {/* Linha 4: Contrato e Valores */}
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label className="text-sm font-semibold">Número do Contrato:</Label>
                      <Input
                        value={formData.numero_contrato}
                        onChange={(e) => setFormData({...formData, numero_contrato: e.target.value})}
                        placeholder="Número do contrato"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Valor da Parcela (R$):</Label>
                      <Input
                        type="text"
                        value={formData.valor_parcela}
                        onChange={(e) => {
                          // Aceita números, ponto e vírgula
                          let value = e.target.value;
                          // Permite apenas números, ponto e vírgula
                          value = value.replace(/[^\d.,]/g, '');
                          // Converte vírgula para ponto para cálculos
                          const numericValue = value.replace(',', '.');
                          handleValorParcelaChange(numericValue);
                        }}
                        placeholder="Ex: 333,55"
                        className="font-medium"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Valor Estim. do Contrato (R$):</Label>
                      <Input
                        type="text"
                        value={formData.valor_liberado}
                        onChange={(e) => {
                          let value = e.target.value;
                          value = value.replace(/[^\d.,]/g, '');
                          const numericValue = value.replace(',', '.');
                          setFormData({...formData, valor_liberado: numericValue});
                        }}
                        placeholder="Ex: 1000,00"
                        className="font-medium"
                      />
                    </div>
                  </div>

                  {/* Linha 5: Taxa, Saldo Devedor e Segmento */}
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label className="text-sm font-semibold">Taxa de Juros (%):</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.taxa_juros}
                        onChange={(e) => handleTaxaJurosChange(e.target.value)}
                        placeholder="1,5"
                      />
                    </div>
                    {/* Seletor de Desconto (aparece quando não tem taxa ou taxa é 0) */}
                    {(!formData.taxa_juros || parseFloat(formData.taxa_juros) === 0) && formData.valor_parcela && formData.parcelas_restantes && (
                      <div>
                        <Label className="text-sm font-semibold">% Desconto:</Label>
                        <Select value={percentualDesconto} onValueChange={handleDescontoChange}>
                          <SelectTrigger className="bg-green-50 border-green-300">
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10% de Desconto</SelectItem>
                            <SelectItem value="20">20% de Desconto</SelectItem>
                            <SelectItem value="30">30% de Desconto</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {/* Saldo Devedor calculado */}
                    <div>
                      <Label className="text-sm font-semibold">
                        Saldo Devedor (R$)
                        {(!formData.taxa_juros || parseFloat(formData.taxa_juros) === 0) && formData.saldo_devedor && formData.parcela_atual && (
                          <span className="text-green-700">
                            {" "}- {percentualDesconto}% desc. - Faixa {(() => {
                              const pagas = parseInt(formData.parcela_atual) || 0;
                              if (pagas <= 10) return "01 a 10";
                              if (pagas <= 20) return "11 a 20";
                              if (pagas <= 30) return "21 a 30";
                              if (pagas <= 40) return "31 a 40";
                              if (pagas <= 60) return "41 a 60";
                              if (pagas <= 70) return "61 a 70";
                              return "71 a 100";
                            })()} parc.
                          </span>
                        )}:
                      </Label>
                      <div 
                        className={`h-10 px-3 py-2 border rounded-md flex items-center text-lg font-bold ${formData.taxa_juros && parseFloat(formData.taxa_juros) > 0 ? "bg-gray-100" : "bg-green-100 border-green-300 text-green-800"}`}
                      >
                        {formData.saldo_devedor ? formatCurrency(parseFloat(formData.saldo_devedor)) : "Calculado automaticamente"}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Tipo de Operação*:</Label>
                      <Select value={formData.tipo_operacao} onValueChange={(v) => setFormData({...formData, tipo_operacao: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {tipoOperacaoOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Simulação de Descontos (quando não tem taxa ou taxa é 0) */}
                  {formData.valor_parcela && formData.parcelas_restantes && (!formData.taxa_juros || parseFloat(formData.taxa_juros) === 0) && (
                    <div className="border rounded-lg p-4 bg-blue-50">
                      <Label className="text-sm font-semibold text-blue-800 mb-3 block">
                        📊 Tabela de Descontos por Faixa de Parcelas Pagas
                      </Label>
                      {(() => {
                        const valorParcela = parseFloat(formData.valor_parcela) || 0;
                        const totalParcelas = parseInt(formData.quantidade_parcelas) || 96;
                        const parcelasPagas = parseInt(formData.parcela_atual) || 0;
                        const vtc = valorParcela * totalParcelas;
                        
                        // Definir as faixas de parcelas pagas
                        const faixas = [
                          { label: "01 a 10", min: 1, max: 10 },
                          { label: "11 a 20", min: 11, max: 20 },
                          { label: "21 a 30", min: 21, max: 30 },
                          { label: "31 a 40", min: 31, max: 40 },
                          { label: "41 a 60", min: 41, max: 60 },
                          { label: "61 a 70", min: 61, max: 70 },
                          { label: "71 a 100", min: 71, max: 100 },
                        ];
                        
                        // Identificar a faixa atual
                        const faixaAtual = faixas.find(f => parcelasPagas >= f.min && parcelasPagas <= f.max);
                        
                        // Se não encontrou faixa atual, não mostra tabela
                        if (!faixaAtual) return null;
                        
                        // Calcular valores apenas para a faixa atual
                        const restantes = totalParcelas - faixaAtual.max;
                        const saldoInicial = vtc * (restantes / totalParcelas);
                        const desc10 = saldoInicial * 0.90;
                        const desc20 = saldoInicial * 0.80;
                        const desc30 = saldoInicial * 0.70;
                        
                        return (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                              <thead>
                                <tr className="bg-blue-100">
                                  <th className="px-2 py-2 text-left font-semibold border">Faixa Atual</th>
                                  <th className="px-2 py-2 text-center font-semibold border">Parc. Restantes</th>
                                  <th className="px-2 py-2 text-right font-semibold border">Saldo Inicial</th>
                                  <th className={`px-2 py-2 text-right font-semibold border ${percentualDesconto === "10" ? "bg-green-200" : ""}`}>10% Desconto</th>
                                  <th className={`px-2 py-2 text-right font-semibold border ${percentualDesconto === "20" ? "bg-green-200" : ""}`}>20% Desconto</th>
                                  <th className={`px-2 py-2 text-right font-semibold border ${percentualDesconto === "30" ? "bg-green-200" : ""}`}>30% Desconto</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="bg-yellow-100 font-bold">
                                  <td className="px-2 py-2 border text-yellow-800">
                                    {faixaAtual.label} parc.
                                  </td>
                                  <td className="px-2 py-2 border text-center">{restantes}</td>
                                  <td className="px-2 py-2 border text-right text-gray-600">
                                    R$ {saldoInicial.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                  </td>
                                  <td className={`px-2 py-2 border text-right ${percentualDesconto === "10" ? "bg-green-300 text-green-900" : "text-green-700"}`}>
                                    R$ {desc10.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                  </td>
                                  <td className={`px-2 py-2 border text-right ${percentualDesconto === "20" ? "bg-green-300 text-green-900" : "text-green-700"}`}>
                                    R$ {desc20.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                  </td>
                                  <td className={`px-2 py-2 border text-right ${percentualDesconto === "30" ? "bg-green-300 text-green-900" : "text-green-700"}`}>
                                    R$ {desc30.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            <p className="text-xs text-blue-600 mt-2">
                              * VTC = R$ {vtc.toLocaleString('pt-BR', {minimumFractionDigits: 2})} | Parcelas Pagas: {parcelasPagas} | <strong>Desconto Selecionado: {percentualDesconto}%</strong>
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Linha 6: Observações */}
                  <div>
                    <Label className="text-sm font-semibold">Observações:</Label>
                    <textarea
                      className="w-full min-h-[80px] px-3 py-2 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                      value={formData.observacoes}
                      onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                      placeholder="Observações adicionais sobre o contrato..."
                    />
                  </div>
                </>
              )}

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Voltar
                </Button>
                {clienteSelecionado && (
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    <Check className="h-4 w-4 mr-2" />
                    Gravar Dados do Contrato Externo
                  </Button>
                )}
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Conteúdo só aparece após selecionar cliente */}
      {clienteSelecionadoPrincipal && (
        <>
          {/* Stats Cards - Componente Refatorado */}
          <TotaisCliente totais={totais} />

          {/* Seção de Simulação - Componente Refatorado */}
          <SimulacaoSection
            simulacoes={simulacoes}
            updateSimulacao={updateSimulacao}
            toggleSimulacao={toggleSimulacao}
            bancos={bancos}
            correspondentes={correspondentes}
            tabelasComissao={tabelasComissao}
            instituicaoSelecionada={instituicaoSelecionada}
            getBancosParaSimulacao={getBancosParaSimulacao}
            getTabelasParaBanco={getTabelasParaBanco}
            getCorrespondentesDoBanco={getCorrespondentesDoBanco}
            analisarCriteriosSimulacao={analisarCriteriosSimulacao}
            formatValorBR={formatValorBR}
            formatValorBROnBlur={formatValorBROnBlur}
          />

          {/* Filtros de Contratos - Componente Refatorado */}
          <ContratosFilter
            search={search}
            setSearch={setSearch}
            tipoFiltro={tipoFiltro}
            setTipoFiltro={setTipoFiltro}
            situacaoFiltro={situacaoFiltro}
            setSituacaoFiltro={setSituacaoFiltro}
            onSearch={handleSearch}
            onRefresh={fetchContratos}
          />

          {/* Lista de Contratos - Componente Refatorado */}
          <ContratosList
            contratos={contratos}
            loading={loading}
            expandedRows={expandedRows}
            onToggleRow={toggleRow}
            onEdit={handleEdit}
            onDelete={handleDelete}
            contratosOcultos={contratosOcultos}
            checklistAnalise={checklistAnalise}
            clienteRestricoesInternas={clienteSelecionadoPrincipal?.acoes_operacionais?.bancosRestricaoInterna}
            percentualDesconto={percentualDesconto}
            onAnalisarContrato={analisarContratoRefin}
            loadingChecklist={loadingChecklist}
          />
        </>
      )}

      {/* Mensagem quando não há cliente selecionado */}
      {!clienteSelecionadoPrincipal && (
        <Card className="border-dashed">
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhum cliente selecionado</p>
              <p className="text-sm">Digite o CPF acima para buscar um cliente e visualizar seus contratos</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
